import commissionApi from 'Agent/Commissions/api/commissionApi';
import {
  changeAdvancedCommissions,
  changeEarnedCommissions,
  CommissionActionType,
  getAdvancedCommissions,
  getEarnedCommissions,
} from 'Agent/Commissions/ducks/commission';
import {Commission} from 'Agent/Commissions/models/Commission';
import {AsyncResult, fromResult, Loading, LoadingAdditional} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, put, takeLatest, delay} from 'redux-saga/effects';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';

export function* getEarnedCommissionsFromApi(action: ReturnType<typeof getEarnedCommissions>) {
  const {filter} = action.payload;
  if (filter) {
    const {agentName, agentNumber} = filter;
    if (agentName !== '' || agentNumber !== '') yield delay(1500);
  }

  if (action.payload.page) {
    action.payload.page.links.forEach(x => {
      if (!x.href.includes('api/commissions')) {
        x.href = 'api/commissions' + x.href;
      }
    });
    yield put(changeEarnedCommissions(new LoadingAdditional(action.payload.page)));
  } else {
    yield put(changeEarnedCommissions(new Loading(action.payload.page)));
  }

  const result: AsyncResult<Page<Commission>> = yield action.payload.page
    ? call(mergeWithNextPage, {existingPage: action.payload.page})
    : call(
        commissionApi.getEarnedCommissions,
        action.payload.agentNumbers,
        action.payload.isNewYork,
        action.payload.encryptedIdentifier,
        action.payload.filter,
        action.payload.sort
      );
  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeEarnedCommissions(value)),
      value => put(changeEarnedCommissions(value))
    )
  );
}

export function* getAdvancedCommissionsFromApi(action: ReturnType<typeof getAdvancedCommissions>) {
  const {filter} = action.payload;
  if (filter) {
    const {agentName, agentNumber} = filter;
    if (agentName !== '' || agentNumber !== '') yield delay(1500);
  }

  if (action.payload.page) {
    action.payload.page.links.forEach(x => {
      if (!x.href.includes('api/commissions')) {
        x.href = 'api/commissions' + x.href;
      }
    });
    yield put(changeAdvancedCommissions(new LoadingAdditional(action.payload.page)));
  } else {
    yield put(changeAdvancedCommissions(new Loading(action.payload.page)));
  }

  const result: AsyncResult<Page<Commission>> = yield action.payload.page
    ? call(mergeWithNextPage, {existingPage: action.payload.page})
    : call(
        commissionApi.getAdvancedCommissions,
        action.payload.agentNumbers,
        action.payload.isNewYork,
        action.payload.encryptedIdentifier,
        action.payload.filter,
        action.payload.sort
      );

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeAdvancedCommissions(value)),
      value => put(changeAdvancedCommissions(value))
    )
  );
}

export function* watchFetchEarnedCommissions() {
  yield takeLatest(CommissionActionType.GET_EARNED_COMMISSIONS, getEarnedCommissionsFromApi);
}

export function* watchFetchAdvancedCommissions() {
  yield takeLatest(CommissionActionType.GET_ADVANCED_COMMISSIONS, getAdvancedCommissionsFromApi);
}

export default function*() {
  yield all([watchFetchAdvancedCommissions(), watchFetchEarnedCommissions()]);
}
